import { useMemo } from 'react';

import { GMetrixTableCellProps, GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { useOrderHistory } from 'pages/orders/order_history/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { Order, OrderStatus } from 'apis/OrderApi';
import { getFormattedDateApi } from 'utils';
import { GMetrixTable } from 'components';
import { Button } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

// The number of results to clamp to before showing the "show all" option
const TRUNCATE_THRESHOLD = 2000;

export const OrderHistoryTable = () => {

    const [state, setState] = useSuperState({
        showAll: false
    });
    const { showAll } = state;

    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { filteredOrders, handleCancelOrderConfirmation, isReceived, isTestingCenter, launchDetailDialog,
        loading, orderStatus } = useOrderHistory();

    const displayedOrders = useMemo(
        () => state.showAll ? filteredOrders : filteredOrders.slice(0, TRUNCATE_THRESHOLD),
        [filteredOrders, state.showAll]
    );

    const truncate = filteredOrders.length > TRUNCATE_THRESHOLD;

    const toggleShowAll = () => setState({ showAll: !state.showAll });

    const DateCell = (cellProps: GMetrixTableCellProps<Order>) => {

        const value = cellProps.dataItem?.[cellProps.field as keyof Order] as string;
        const formatted = value ? getFormattedDateApi(new Date(value)) : '';

        return (
            <td>
                {formatted}
            </td>
        );

    };

    const TypeCell = (cellProps: GMetrixTableCellProps<Order>) => {

        const value = cellProps.dataItem?.UsesByType ?? '';
        const lines = value.split('<br />').map(l => l.trim()).filter(Boolean);
        const displayLines = lines.length > 0 ? lines : [formatMessage({ id: 'not_included.aafc6471', defaultMessage: 'Not Included' })];

        return (
            <td>
                <ul className="list-unstyled m-0">
                    {displayLines.map((line, key) => (
                        <li key={key}>
                            {line}
                        </li>
                    ))}
                </ul>
            </td>
        );

    };

    const ActionCell = (cellProps: GMetrixTableCellProps<Order>) => (
        <td style={{ whiteSpace: 'nowrap', width: '1%' }}>
            <GMetrixButton
                disabled={loading}
                onClick={() => launchDetailDialog(cellProps.dataItem)}
                size={'sm'}
                variant="primary"
            >
                {<FormattedMessage defaultMessage="Details" id="details.7799b923" />}
            </GMetrixButton>
            {orderStatus === OrderStatus.Pending && (
                <GMetrixButton
                    className="ms-1"
                    disabled={loading}
                    onClick={() => handleCancelOrderConfirmation(cellProps.dataItem.ID)}
                    size={'sm'}
                    variant="danger"
                >
                    {<FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />}
                </GMetrixButton>
            )}
        </td>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo((): GMetrixTableColumnProps<Order>[] => [
        { cell: DateCell, field: 'DateCreate', title: formatMessage({ id: 'ordered.61d11bd3', defaultMessage: 'Ordered' }) },
        { cell: DateCell, field: 'DateExpire', title: formatMessage({ id: 'expiration.c3d7aca4', defaultMessage: 'Expiration' }) },
        { field: 'Partner.Name' as any, hidden: false, title: formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' }) },
        { field: 'License.Name' as any, title: formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' }) },
        { field: 'NumGranted', title: formatMessage({ id: 'assign.0dabb6f2', defaultMessage: 'Assign' }) },
        { field: 'NumAvailable', hidden: isTestingCenter, title: formatMessage({ id: 'available.d321561a', defaultMessage: 'Available' }) },
        { cell: TypeCell, field: 'UsesByType', hidden: !isTestingCenter && !isReceived, title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
        { field: 'PurchaseOrder', title: formatMessage({ id: 'purchase_order.17844493', defaultMessage: 'Purchase Order' }) },
        { field: 'Comment', hidden: highestRole.id !== 1, title: formatMessage({ id: 'comment.252dd7a3', defaultMessage: 'Comment', description: 'Formulario' }) },
        { field: 'OrderControl', hidden: true, title: formatMessage({ id: 'order_control.4d0b6033', defaultMessage: 'Order Control' }) },
        { field: 'FilledByPartnerName', hidden: true, title: formatMessage({ id: 'filled_by_partner.1e91faff', defaultMessage: 'Filled By Partner' }) },
        { field: 'OrderedBy', hidden: true, title: formatMessage({ id: 'ordered_by.381902ec', defaultMessage: 'Ordered By', description: 'Formulario' }) },
        { cell: ActionCell, title: '', width: '1%' }
    ], [highestRole.id, isReceived, isTestingCenter, loading, orderStatus]);

    return (
        <div className={'order-history-table'}>

            {truncate && (
                <div className="my-3">
                    {showAll
                        ? (
                            <div className={'d-flex align-items-center gap-1'}>
                                <span>
                                    {<FormattedMessage defaultMessage="Showing All Orders." id="showing_all_orders.a6e5bf49" />}
                                </span>
                                <GMetrixButton
                                    className={'p-0'}
                                    onClick={toggleShowAll}
                                    size={'sm'}
                                    variant={'link'}
                                >
                                    {<FormattedMessage defaultMessage="Show Less" id="show_less.e31d9ad0" />}
                                </GMetrixButton>
                            </div>
                        )
                        : (
                            <div className={'d-flex align-items-center gap-1'}>
                                <span>
                                    {formatMessage({ id: 'showing_latest_threshold_orders.93f0714b', defaultMessage: 'Showing latest {threshold} orders.' }, { threshold: TRUNCATE_THRESHOLD })}
                                </span>

                                <GMetrixButton
                                    className={'p-0'}
                                    onClick={toggleShowAll}
                                    size={'sm'}
                                    variant={'link'}
                                >
                                    {<FormattedMessage defaultMessage="Show All" id="show_all.04dd2aca" />}
                                </GMetrixButton>
                            </div>
                        )
                    }
                </div>
            )}

            <GMetrixTable
                allowExport
                columnData={columns as any}
                defaultItemsPerPage={20}
                excelExportFileName={formatMessage({ id: 'orderhistory.70ce2d44', defaultMessage: 'OrderHistory' })}
                loading={loading}
                rowData={displayedOrders}
                tableName={formatMessage({ id: 'orderhistory.70ce2d44', defaultMessage: 'OrderHistory' })}
            />
        </div>
    );

};
